/**
 * Generation Progression Hook
 * 
 * Manages the complete progression state for video generation,
 * tracking all stages from image upload to video completion.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  GenerationStage,
  GenerationProgression,
  STAGE_CONFIG,
  calculateOverallProgress,
} from '../types/progression';

export interface UseGenerationProgressionOptions {
  onStageChange?: (stage: GenerationStage, progress: number) => void;
  onProgress?: (overallProgress: number) => void;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

export interface UseGenerationProgressionReturn {
  progression: GenerationProgression;
  setStage: (stage: GenerationStage, stageProgress?: number, message?: string) => void;
  updateStageProgress: (progress: number, message?: string) => void;
  setError: (error: string) => void;
  reset: () => void;
}

const INITIAL_PROGRESSION: GenerationProgression = {
  currentStage: 'idle',
  overallProgress: 0,
  currentStageProgress: 0,
  message: 'Ready to start',
  stages: {},
  startedAt: undefined,
  completedAt: undefined,
};

export function useGenerationProgression(
  options: UseGenerationProgressionOptions = {}
): UseGenerationProgressionReturn {
  const { onStageChange, onProgress, onComplete, onError } = options;

  const [progression, setProgression] =
    useState<GenerationProgression>(INITIAL_PROGRESSION);

  const progressionRef = useRef(progression);
  progressionRef.current = progression;

  // Notify callbacks when progression changes
  useEffect(() => {
    if (onProgress) {
      onProgress(progression.overallProgress);
    }
  }, [progression.overallProgress, onProgress]);

  useEffect(() => {
    if (progression.currentStage === 'completed' && onComplete) {
      onComplete();
    }
  }, [progression.currentStage, onComplete]);

  useEffect(() => {
    if (progression.error && onError) {
      onError(progression.error);
    }
  }, [progression.error, onError]);

  const setStage = useCallback(
    (
      stage: GenerationStage,
      stageProgress: number = 0,
      message?: string
    ) => {
      // Validate stage to prevent invalid states
      if (!STAGE_CONFIG[stage]) {
        console.error(`❌ [Progression] Invalid stage: ${stage}, defaulting to 'generating_video'`);
        stage = 'generating_video';
      }
      
      // Clamp progress to valid range
      const clampedProgress = Math.max(0, Math.min(100, stageProgress));
      
      setProgression((prev) => {
        const config = STAGE_CONFIG[stage];
        const stageMessage =
          message || config?.defaultMessage || 'Processing...';

        const newStages = {
          ...prev.stages,
          [stage]: {
            started: true,
            completed: stage === 'completed' || stage === 'failed',
            progress: clampedProgress,
            message: stageMessage,
            timestamp: Date.now(),
          },
        };

        // Mark previous stages as completed
        const stageOrder: GenerationStage[] = [
          'idle',
          'uploading_image',
          'storing_image_s3',
          'storing_image_db',
          'enhancing_prompt',
          'starting_generation',
          'generating_video',
          'storing_video_s3',
          'storing_video_db',
          'completed',
        ];

        const currentIndex = stageOrder.indexOf(stage);
        if (currentIndex > 0) {
          for (let i = 0; i < currentIndex; i++) {
            const prevStage = stageOrder[i];
            if (newStages[prevStage] && !newStages[prevStage]!.completed) {
              newStages[prevStage] = {
                ...newStages[prevStage]!,
                completed: true,
                progress: 100,
              };
            }
          }
        }

        const overallProgress = calculateOverallProgress(stage, clampedProgress);

        const newProgression: GenerationProgression = {
          ...prev,
          currentStage: stage,
          overallProgress,
          currentStageProgress: clampedProgress,
          message: stageMessage,
          stages: newStages,
          startedAt: prev.startedAt || (stage !== 'idle' ? Date.now() : undefined),
          completedAt:
            stage === 'completed' || stage === 'failed'
              ? Date.now()
              : prev.completedAt,
        };

        if (onStageChange) {
          onStageChange(stage, overallProgress);
        }

        return newProgression;
      });
    },
    [onStageChange]
  );

  const updateStageProgress = useCallback(
    (progress: number, message?: string) => {
      // Clamp progress to valid range
      const clampedProgress = Math.max(0, Math.min(100, progress));
      
      setProgression((prev) => {
        // Validate current stage exists
        if (!STAGE_CONFIG[prev.currentStage]) {
          console.error(`❌ [Progression] Invalid current stage: ${prev.currentStage}, skipping update`);
          return prev;
        }
        
        const config = STAGE_CONFIG[prev.currentStage];
        const overallProgress = calculateOverallProgress(
          prev.currentStage,
          clampedProgress
        );
        const stageMessage =
          message || config?.defaultMessage || prev.message;

        const newStages = {
          ...prev.stages,
          [prev.currentStage]: {
            started: true,
            completed: false,
            progress,
            message: stageMessage,
            timestamp: prev.stages[prev.currentStage]?.timestamp || Date.now(),
          },
        };

        return {
          ...prev,
          overallProgress,
          currentStageProgress: progress,
          message: stageMessage,
          stages: newStages,
        };
      });
    },
    []
  );

  const setError = useCallback((error: string) => {
    setProgression((prev) => ({
      ...prev,
      currentStage: 'failed',
      error,
      message: `Error: ${error}`,
      completedAt: Date.now(),
      stages: {
        ...prev.stages,
        failed: {
          started: true,
          completed: true,
          progress: 0,
          message: error,
          timestamp: Date.now(),
        },
      },
    }));
  }, []);

  const reset = useCallback(() => {
    setProgression(INITIAL_PROGRESSION);
  }, []);

  return {
    progression,
    setStage,
    updateStageProgress,
    setError,
    reset,
  };
}


